import {
  Box,
  Card,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import type { CashRegisterSummary } from "../cash-register-summary";
import { cashHealthHint } from "../cash-register-summary";

const money = (n: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n);

type CashRegisterReportViewProps = {
  summary: CashRegisterSummary;
  /** Título opcional (ex.: fechamento vs histórico) */
  title?: string;
  /** Mostrar bloco de saúde / conferência */
  showHealth?: boolean;
};

export function CashRegisterReportView({
  summary,
  title = "Resumo do caixa",
  showHealth = true,
}: CashRegisterReportViewProps) {
  const theme = useTheme();
  const hint = cashHealthHint(summary.differenceApprox);
  const Icon =
    hint.severity === "success" ?
      CheckCircleOutlineIcon :
      hint.severity === "warning" ?
        WarningAmberOutlinedIcon :
        InfoOutlinedIcon;

  return (
    <Stack spacing={2}>
      {title ? (
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>
      ) : null}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} flexWrap="wrap" useFlexGap>
        <Box
          sx={{
            flex: "1 1 140px",
            p: 2,
            borderRadius: 2,
            border: 1,
            borderColor: "divider",
            bgcolor: alpha(theme.palette.primary.main, 0.04),
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Abertura (dinheiro)
          </Typography>
          <Typography variant="h6" fontWeight={800}>
            {money(summary.openingCash)}
          </Typography>
        </Box>
        <Box
          sx={{
            flex: "1 1 140px",
            p: 2,
            borderRadius: 2,
            border: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Vendas no caixa (total)
          </Typography>
          <Typography variant="h6" fontWeight={800}>
            {summary.totalSellsInSession}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {summary.countOpenSales} em aberto · {summary.countCompletedSales} concluídas
          </Typography>
        </Box>
        <Box
          sx={{
            flex: "1 1 140px",
            p: 2,
            borderRadius: 2,
            border: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Total vendido (todas as formas)
          </Typography>
          <Typography variant="h6" fontWeight={800}>
            {money(summary.totalSales)}
          </Typography>
        </Box>
      </Stack>

      <Card elevation={0} variant="outlined">
        <CardContent sx={{ py: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Por forma de pagamento
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Forma</TableCell>
                  <TableCell align="right">Qtd</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.breakdown
                  .filter((r) => r.count > 0)
                  .map((r) => (
                    <TableRow key={r.method}>
                      <TableCell>{r.label}</TableCell>
                      <TableCell align="right">{r.count}</TableCell>
                      <TableCell align="right">{money(r.total)}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.info.main, 0.06),
          border: 1,
          borderColor: alpha(theme.palette.info.main, 0.25),
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Vendas em dinheiro
          </Typography>
          <Typography variant="body1" fontWeight={700}>
            {money(summary.totalCashSales)}
          </Typography>
        </Box>
        {(summary.totalEntries > 0 || summary.totalWithdrawals > 0) && (
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Entradas / Retiradas
            </Typography>
            <Typography variant="body2" fontWeight={700} color="success.main">
              +{money(summary.totalEntries)}
            </Typography>
            <Typography variant="body2" fontWeight={700} color="error.main">
              −{money(summary.totalWithdrawals)}
            </Typography>
          </Box>
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Caixa esperado
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Abertura + entradas − retiradas + dinheiro
          </Typography>
          <Typography variant="body1" fontWeight={700}>
            {money(summary.expectedCashApprox)}
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Valor contado no fechamento
          </Typography>
          <Typography variant="body1" fontWeight={700}>
            {summary.closingCash !== null && summary.closingCash !== undefined ?
              money(summary.closingCash) :
              "—"}
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Diferença (contado − esperado)
          </Typography>
          <Typography
            variant="body1"
            fontWeight={700}
            color={
              summary.differenceApprox === null ? "text.primary" :
              Math.abs(summary.differenceApprox) < 0.02 ? "success.main" : "error.main"
            }
          >
            {summary.differenceApprox !== null && summary.differenceApprox !== undefined ?
              money(summary.differenceApprox) :
              "—"}
          </Typography>
        </Box>
      </Stack>

      {showHealth ? (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Icon
            color={hint.severity === "success" ? "success" : "action"}
            fontSize="small"
            style={{ marginTop: 2 }}
          />
          <Typography variant="body2" color="text.secondary">
            {hint.label}
          </Typography>
        </Stack>
      ) : null}
    </Stack>
  );
}
